import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { Palette, ArrowRight, Upload, Trash2, ImageIcon, FileArchive, UserPlus, Pencil, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";

export default function Settings() {
    const { activeOrg, user, refresh } = useAuth();
    const [org, setOrg] = useState(null);
    const [users, setUsers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [logoBust, setLogoBust] = useState(0);
    const fileRef = useRef(null);

    useEffect(() => {
        api.get("/org/current").then(({ data }) => setOrg(data));
        api.get("/users").then(({ data }) => setUsers(data));
    }, [activeOrg?.id]);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/org/current", org);
            toast.success("Saved"); await refresh();
        } finally { setBusy(false); }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        if (!file.name.match(/\.(png|jpe?g)$/i)) return toast.error("PNG or JPG only");
        const fd = new FormData(); fd.append("file", file);
        const token = localStorage.getItem("vyastra_token");
        try {
            const r = await fetch(`${API}/org/current/logo`, {
                method: "POST", body: fd,
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
            const data = await r.json();
            setOrg((o) => ({ ...o, logo_path: data.logo_path }));
            setLogoBust(Date.now());
            toast.success("Logo uploaded — invoice PDFs will use it from the next download.");
        } catch (e) { toast.error(e.message || "Upload failed"); }
    };

    const removeLogo = async () => {
        if (!confirm("Remove organization logo? Future PDFs will fall back to the Vyaahar mark.")) return;
        await api.delete("/org/current/logo");
        setOrg((o) => ({ ...o, logo_path: null }));
        setLogoBust(Date.now());
        toast.success("Logo removed");
    };

    if (!org) return <div className="p-8 text-muted-foreground">Loading…</div>;

    return (
        <>
            <PageHeader kicker="WORKSPACE" title="Settings" subtitle="Organization, users, currencies, and preferences." />
            <PageBody>
                <Tabs defaultValue="org" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="org" data-testid="settings-tab-org">Organization</TabsTrigger>
                        <TabsTrigger value="users" data-testid="settings-tab-users">Users & roles</TabsTrigger>
                        <TabsTrigger value="prefs" data-testid="settings-tab-prefs">Preferences</TabsTrigger>
                        <TabsTrigger value="branding" data-testid="settings-tab-branding">Branding</TabsTrigger>
                        <TabsTrigger value="period-lock" data-testid="settings-tab-period-lock">Period lock</TabsTrigger>
                        {activeOrg?.is_demo && <TabsTrigger value="demo" data-testid="settings-tab-demo">Demo data</TabsTrigger>}
                    </TabsList>
                    <TabsContent value="org">
                        <div className="space-y-4">
                            {/* Logo upload card */}
                            <div className="bg-card border border-border rounded-md p-6">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">COMPANY LOGO</div>
                                <div className="flex items-start gap-6">
                                    <div className="w-[180px] h-[90px] border border-dashed border-border rounded-md grid place-items-center bg-muted/40 overflow-hidden shrink-0">
                                        {org.logo_path ? (
                                            <img src={`${API}/static/logo/${activeOrg?.id}?t=${logoBust}`} alt="logo" className="max-w-full max-h-full object-contain" data-testid="org-logo-preview" />
                                        ) : (
                                            <div className="flex flex-col items-center gap-1 text-muted-foreground">
                                                <ImageIcon className="w-6 h-6" strokeWidth={1.4} />
                                                <span className="text-[10.5px] uppercase tracking-[0.12em]">No logo set</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[13px]">Upload your company logo (PNG or JPG). It will appear on every invoice PDF and statement.</p>
                                        <p className="text-[11.5px] text-muted-foreground mt-1">Recommended: 600×200 px transparent PNG. Renders at ~42mm wide.</p>
                                        <div className="flex items-center gap-2 mt-3">
                                            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="org-logo-input" />
                                            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="org-logo-upload"><Upload className="w-3.5 h-3.5 mr-1.5" />{org.logo_path ? "Replace" : "Upload"} logo</Button>
                                            {org.logo_path && <Button variant="ghost" size="sm" onClick={removeLogo} data-testid="org-logo-remove"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove</Button>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-card border border-border rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Display name" value={org.name} onChange={(v) => setOrg({ ...org, name: v })} />
                                <Field label="Legal name" value={org.legal_name} onChange={(v) => setOrg({ ...org, legal_name: v })} />
                                <Field label="GSTIN" value={org.gstin} onChange={(v) => setOrg({ ...org, gstin: v })} />
                                <Field label="PAN" value={org.pan} onChange={(v) => setOrg({ ...org, pan: v })} />
                                <Field label="Industry" value={org.industry} onChange={(v) => setOrg({ ...org, industry: v })} />
                                <Field label="Base currency" value={org.base_currency} onChange={(v) => setOrg({ ...org, base_currency: v })} />
                                <Field label="Phone" value={org.phone} onChange={(v) => setOrg({ ...org, phone: v })} />
                                <Field label="Email" value={org.email} onChange={(v) => setOrg({ ...org, email: v })} />
                                <Field label="Website" value={org.website} onChange={(v) => setOrg({ ...org, website: v })} span={2} />
                                <div className="col-span-2 flex justify-end"><Button onClick={save} disabled={busy} data-testid="settings-save">{busy ? "Saving…" : "Save changes"}</Button></div>
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="users">
                        <UsersPanel users={users} setUsers={setUsers} currentUserId={user?.id} />
                    </TabsContent>
                    <TabsContent value="prefs">
                        <div className="bg-card border border-border rounded-md p-6 space-y-3">
                            <h3 className="text-[17px] font-semibold">Preferences</h3>
                            <p className="text-[13px] text-muted-foreground">Numbering sequences, document templates, notification rules — all configurable here.</p>
                            <div className="grid grid-cols-2 gap-3 mt-4">
                                <div className="border border-border rounded-md p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Invoice prefix</div><div className="font-mono text-[15px] mt-1">INV-#####</div></div>
                                <div className="border border-border rounded-md p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Bill prefix</div><div className="font-mono text-[15px] mt-1">BILL-#####</div></div>
                                <div className="border border-border rounded-md p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">FY Start</div><div className="font-mono text-[15px] mt-1">{org.fiscal_year_start}</div></div>
                                <div className="border border-border rounded-md p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Currencies</div><div className="font-mono text-[15px] mt-1">{org.base_currency}</div></div>
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="branding">
                        <div className="space-y-4">
                            <Link to="/settings/invoice-templates" className="block bg-card border border-border rounded-md p-6 hover:border-[#a68a56]/40 transition" data-testid="settings-templates-link">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-md bg-[#a68a56]/12 grid place-items-center shrink-0">
                                        <Palette className="w-6 h-6 text-[#a68a56]" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-base font-semibold">Invoice Templates</h3>
                                        <p className="text-[12.5px] text-muted-foreground mt-0.5">Brand your invoices with custom colors, logo, footer, and terms. Set one as default and override per invoice.</p>
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                                </div>
                            </Link>

                            <ImageUploadCard
                                title="Authorised Signatory"
                                kicker="SIGNATURE"
                                description="Upload a PNG/JPG of the authorised signatory's handwritten signature. It will appear on every invoice PDF above the 'Authorized Signatory' label."
                                hint="Recommended: 400×120 px transparent PNG. Max 2 MB."
                                endpoint="/org/current/signature"
                                staticPath="signature"
                                imagePath={org.signature_path}
                                bust={logoBust}
                                orgId={activeOrg?.id}
                                onUploaded={(p) => setOrg((o) => ({ ...o, signature_path: p }))}
                                onRemoved={() => setOrg((o) => ({ ...o, signature_path: null }))}
                                bumpBust={() => setLogoBust(Date.now())}
                                testid="org-sig"
                            />

                            <ImageUploadCard
                                title="Company Seal / Stamp"
                                kicker="SEAL"
                                description="Upload a PNG/JPG of your company seal/stamp. It will be rendered next to the signature on invoice PDFs."
                                hint="Recommended: square transparent PNG, ~300×300 px. Max 2 MB."
                                endpoint="/org/current/seal"
                                staticPath="seal"
                                imagePath={org.seal_path}
                                bust={logoBust}
                                orgId={activeOrg?.id}
                                onUploaded={(p) => setOrg((o) => ({ ...o, seal_path: p }))}
                                onRemoved={() => setOrg((o) => ({ ...o, seal_path: null }))}
                                bumpBust={() => setLogoBust(Date.now())}
                                testid="org-seal"
                            />
                        </div>
                    </TabsContent>
                    <TabsContent value="period-lock">
                        <PeriodLockPanel />
                    </TabsContent>
                    {activeOrg?.is_demo && (
                        <TabsContent value="demo">
                            <DemoDataPanel />
                        </TabsContent>
                    )}
                </Tabs>
            </PageBody>
        </>
    );
}

function Field({ label, value, onChange, span = 1 }) {
    return (
        <div className={span === 2 ? "col-span-2" : "col-span-1"}>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{label}</Label>
            <Input className="mt-1.5" value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </div>
    );
}

function ImageUploadCard({ title, kicker, description, hint, endpoint, staticPath, imagePath, bust, orgId, onUploaded, onRemoved, bumpBust, testid }) {
    const inputRef = useRef(null);
    const upload = async (file) => {
        if (!file) return;
        if (!file.name.match(/\.(png|jpe?g)$/i)) return toast.error("PNG or JPG only");
        if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
        const fd = new FormData(); fd.append("file", file);
        const token = localStorage.getItem("vyastra_token");
        try {
            const r = await fetch(`${API}${endpoint}`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
            const data = await r.json();
            onUploaded(data.signature_path || data.seal_path);
            bumpBust();
            toast.success(`${title} uploaded — invoice PDFs will use it from the next download.`);
        } catch (e) { toast.error(e.message || "Upload failed"); }
    };
    const remove = async () => {
        if (!confirm(`Remove ${title.toLowerCase()}?`)) return;
        await api.delete(endpoint);
        onRemoved();
        bumpBust();
        toast.success(`${title} removed`);
    };
    return (
        <div className="bg-card border border-border rounded-md p-6" data-testid={`${testid}-card`}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">{kicker}</div>
            <div className="flex items-start gap-6">
                <div className="w-[180px] h-[90px] border border-dashed border-border rounded-md grid place-items-center bg-muted/40 overflow-hidden shrink-0">
                    {imagePath ? (
                        <img src={`${API}/static/${staticPath}/${orgId}?t=${bust}`} alt={title} className="max-w-full max-h-full object-contain" data-testid={`${testid}-preview`} />
                    ) : (
                        <div className="flex flex-col items-center gap-1 text-muted-foreground">
                            <ImageIcon className="w-6 h-6" strokeWidth={1.4} />
                            <span className="text-[10.5px] uppercase tracking-[0.12em]">Not set</span>
                        </div>
                    )}
                </div>
                <div className="flex-1">
                    <h3 className="text-base font-semibold">{title}</h3>
                    <p className="text-[13px] mt-0.5">{description}</p>
                    <p className="text-[11.5px] text-muted-foreground mt-1">{hint}</p>
                    <div className="flex items-center gap-2 mt-3">
                        <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
                        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload`}>
                            <Upload className="w-3.5 h-3.5 mr-1.5" />{imagePath ? "Replace" : "Upload"}
                        </Button>
                        {imagePath && <Button variant="ghost" size="sm" onClick={remove} data-testid={`${testid}-remove`}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove</Button>}
                    </div>
                </div>
            </div>
        </div>
    );
}

const ROLES = ["Founder", "Admin", "Accountant", "Sales", "Viewer", "Super Admin"];

function UsersPanel({ users, setUsers, currentUserId }) {
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    const open = (u) => {
        setForm(u ? { ...u, password: "" } : { name: "", username: "", email: "", role: "Accountant", password: "" });
        setDrawer({ open: true, item: u });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim() || !form.username?.trim()) return toast.error("Name and username required");
        if (!drawer.item && !form.password) return toast.error("Password required for new users");
        setBusy(true);
        try {
            const payload = { ...form };
            if (!payload.password) delete payload.password;
            if (drawer.item) {
                const { data } = await api.put(`/users/${drawer.item.id}`, payload);
                setUsers((u) => u.map((x) => x.id === data.id ? data : x));
            } else {
                const { data } = await api.post("/users", payload);
                setUsers((u) => [...u, data]);
            }
            toast.success(drawer.item ? "User updated" : "User created");
            setDrawer({ open: false, item: null });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const remove = async (u) => {
        if (u.id === currentUserId) return toast.error("You cannot delete your own account");
        if (!confirm(`Remove ${u.name} from this organization?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            setUsers((all) => all.filter((x) => x.id !== u.id));
            toast.success("User removed");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <>
            <div className="flex justify-end mb-3">
                <Button onClick={() => open(null)} data-testid="user-new"><UserPlus className="w-4 h-4 mr-1.5" />Invite user</Button>
            </div>
            <div className="bg-card border border-border rounded-md overflow-hidden">
                <table className="fin-table">
                    <thead><tr><th>Name</th><th>Username</th><th>Email</th><th>Role</th><th></th></tr></thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                <td className="font-medium">{u.name} {u.id === currentUserId && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">(you)</span>}</td>
                                <td className="font-mono text-[12px]">{u.username}</td>
                                <td>{u.email}</td>
                                <td><span className="text-[11px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-md bg-muted border border-border">{u.role}</span></td>
                                <td className="text-right">
                                    <div className="inline-flex items-center gap-1.5">
                                        <button onClick={() => open(u)} className="inline-flex items-center justify-center w-7 h-7 rounded border border-border hover:border-foreground transition" data-testid={`user-edit-${u.id}`} title="Edit">
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => remove(u)} disabled={u.id === currentUserId} className="inline-flex items-center justify-center w-7 h-7 rounded border border-border hover:border-destructive hover:text-destructive transition disabled:opacity-30 disabled:hover:border-border disabled:hover:text-current" data-testid={`user-delete-${u.id}`} title="Delete">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Sheet open={drawer.open} onOpenChange={(v) => !v && setDrawer({ open: false, item: null })}>
                <SheetContent side="right" className="w-full sm:max-w-[460px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4">
                        <SheetTitle className="font-display text-xl font-light">{drawer.item ? "Edit user" : "Invite user"}</SheetTitle>
                    </SheetHeader>
                    <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-4">
                        <div><Label>Display name <span className="text-destructive">*</span></Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name" required /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Username <span className="text-destructive">*</span></Label><Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5 font-mono" data-testid="user-username" required /></div>
                            <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email" /></div>
                        </div>
                        <div>
                            <Label>Role</Label>
                            <select value={form.role || "Accountant"} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full h-9 px-3 rounded-md border border-input bg-background text-[13px]" data-testid="user-role">
                                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>{drawer.item ? "New password (leave empty to keep)" : "Password"} {!drawer.item && <span className="text-destructive">*</span>}</Label>
                            <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-password" autoComplete="new-password" placeholder={drawer.item ? "•••••••• (unchanged)" : "Min 6 characters"} />
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-border">
                            <Button type="submit" disabled={busy} data-testid="user-save">{busy ? "Saving…" : "Save"}</Button>
                            <Button type="button" variant="outline" onClick={() => setDrawer({ open: false, item: null })}>Cancel</Button>
                        </div>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}


function PeriodLockPanel() {
    const [lock, setLock] = useState({ lock_date: "", reason: "" });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/period-lock");
            setLock({ lock_date: data.lock_date || "", reason: data.reason || "" });
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const save = async () => {
        if (!lock.lock_date) return toast.error("Pick a lock date");
        if (!confirm(`Lock all books on or before ${lock.lock_date}? Posting will be refused for any date in the locked period.`)) return;
        setBusy(true);
        try {
            await api.post("/period-lock", lock);
            toast.success("Period locked");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to lock period");
        } finally { setBusy(false); }
    };

    const clearLock = async () => {
        if (!confirm("Remove period lock? Editing/posting in past dates will be allowed again.")) return;
        setBusy(true);
        try {
            await api.post("/period-lock/clear");
            toast.success("Period unlocked");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to unlock");
        } finally { setBusy(false); }
    };

    if (loading) return <div className="text-muted-foreground p-4">Loading…</div>;

    return (
        <div className="bg-card border border-border rounded-md p-6 max-w-2xl" data-testid="period-lock-panel">
            <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-md bg-[#a68a56]/12 grid place-items-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-[#a68a56]" />
                </div>
                <div className="flex-1">
                    <h3 className="text-base font-semibold">Period lock</h3>
                    <p className="text-[12.5px] text-muted-foreground mt-0.5">
                        After auditor sign-off, lock all journals on or before a date. Once locked, posting/editing in the locked period is refused.
                    </p>
                </div>
            </div>
            {lock.lock_date ? (
                <div className="bg-positive/10 border border-positive/30 rounded p-4 mb-4">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-positive font-semibold">CURRENTLY LOCKED</div>
                    <div className="font-display text-xl mt-1">All entries on or before {lock.lock_date} are locked</div>
                    {lock.reason && <div className="text-[12.5px] text-muted-foreground mt-1">Reason: {lock.reason}</div>}
                </div>
            ) : (
                <div className="bg-muted/40 rounded p-4 mb-4 text-[12.5px] text-muted-foreground">Books are currently <strong>open</strong> — entries can be posted/edited in any past date.</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Lock date</Label>
                    <Input type="date" className="mt-1.5" value={lock.lock_date || ""} onChange={(e) => setLock({ ...lock, lock_date: e.target.value })} data-testid="period-lock-date" />
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Reason (optional)</Label>
                    <Input className="mt-1.5" value={lock.reason || ""} onChange={(e) => setLock({ ...lock, reason: e.target.value })} placeholder="FY 24-25 audit closed" data-testid="period-lock-reason" />
                </div>
            </div>
            <div className="flex gap-2">
                <Button onClick={save} disabled={busy} data-testid="period-lock-save">{busy ? "Saving…" : (lock.lock_date ? "Update lock" : "Lock period")}</Button>
                {lock.lock_date && (
                    <Button variant="outline" onClick={clearLock} disabled={busy} data-testid="period-lock-clear" className="text-destructive">Clear lock</Button>
                )}
            </div>
        </div>
    );
}

function DemoDataPanel() {
    const [busy, setBusy] = useState(false);
    const [months, setMonths] = useState(24);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!confirm(`Wipe and regenerate ${months} months of realistic demo books? This will clear all invoices, bills, payments, and journals for this demo org.`)) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await api.post("/dev/seed-demo-realistic", { months, seed: Math.floor(Math.random() * 10000) });
            setResult(r.data);
            toast.success(`Regenerated ${r.data.invoices} invoices, ${r.data.bills} bills, ${r.data.payments_received + r.data.payments_made} payments`);
            // Force a hard reload to refresh all caches
            setTimeout(() => window.location.reload(), 1500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Seed failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-card border border-border p-6" style={{ borderRadius: 2 }}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-gold mb-3 font-mono">DEMO DATA · ADMINS ONLY</div>
            <h3 className="font-display text-2xl font-light mb-2">Regenerate realistic books</h3>
            <p className="text-[13px] text-muted-foreground mb-5 max-w-2xl">
                Wipe and re-seed this demo workspace with a fresh, believable mid-size SME ledger:
                30 customers, 15 vendors, seasonal invoice patterns, realistic collection lags,
                bank transactions, and a clean trial balance.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mb-5">
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Months of history</Label>
                    <Input type="number" min={6} max={36} value={months} onChange={(e) => setMonths(parseInt(e.target.value || "24", 10))} className="mt-1.5" data-testid="demo-months" />
                    <p className="text-[10.5px] text-muted-foreground mt-1 font-mono">6–36 months. Default 24.</p>
                </div>
            </div>

            <Button onClick={run} disabled={busy} data-testid="demo-reseed">
                {busy ? "Generating realistic books…" : "Wipe + Regenerate demo data"}
            </Button>

            {result && (
                <div className="mt-6 border border-border bg-background p-4 font-mono text-[12px]" style={{ borderRadius: 2 }} data-testid="demo-result">
                    <div className="text-gold mb-2">{`> seed-demo-realistic — complete`}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Stat label="Customers" value={result.customers} />
                        <Stat label="Vendors" value={result.vendors} />
                        <Stat label="Invoices" value={result.invoices} />
                        <Stat label="Bills" value={result.bills} />
                        <Stat label="Pmts Recv" value={result.payments_received} />
                        <Stat label="Pmts Made" value={result.payments_made} />
                        <Stat label="Bank txns" value={result.bank_transactions} />
                        <Stat label="Journals" value={result.journal_entries} />
                    </div>
                    <div className="text-muted-foreground mt-3 text-[11px]">Refreshing page in 1.5s…</div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
            <div className="font-mono tabular-nums text-[18px] font-medium text-foreground">{value}</div>
        </div>
    );
}
